import * as THREE from 'three';
import { blockTypeById } from './blocks';
import {
  BASE_DEPTH,
  BLOCK_AIR,
  BLOCK_RENDER_DISTANCE_CHUNKS,
  CHUNK_SIZE,
  WORLD_HEIGHT,
  WORLD_MIN_Y,
  WORLD_SIZE_X,
  WORLD_SIZE_Z,
} from './constants';

const CHUNKS_X = Math.ceil(WORLD_SIZE_X / CHUNK_SIZE);
const CHUNKS_Z = Math.ceil(WORLD_SIZE_Z / CHUNK_SIZE);
/** Enough instances for any runtime render-distance up to the full world. */
const MAX_VISIBLE_CHUNKS = CHUNKS_X * CHUNKS_Z;
const Y_SIZE = WORLD_HEIGHT - WORLD_MIN_Y;

interface MaskCell {
  type: number;
  normal: 1 | -1;
}

/**
 * Hybrid terrain renderer:
 * - untouched chunks are cheap slab instances (one draw call);
 * - chunks containing sparse overrides are greedy-meshed;
 * - only chunks inside the global render distance are present.
 */
export class ChunkRenderer {
  readonly group = new THREE.Group();

  private readonly edited = new Set<number>();
  private readonly dirty = new Set<number>();
  private readonly meshes = new Map<number, THREE.Mesh>();
  private readonly slabMesh: THREE.InstancedMesh;
  private readonly chunkMaterial: THREE.MeshLambertMaterial;
  private readonly tmpMatrix = new THREE.Matrix4();
  private readonly tmpPosition = new THREE.Vector3();
  private readonly tmpScale = new THREE.Vector3(CHUNK_SIZE, BASE_DEPTH, CHUNK_SIZE);
  private readonly tmpQuaternion = new THREE.Quaternion();

  private centerChunkX = -1;
  private centerChunkZ = -1;
  private visibilityDirty = true;
  private renderDistanceChunks = BLOCK_RENDER_DISTANCE_CHUNKS;

  constructor(
    private readonly getBlock: (x: number, y: number, z: number) => number,
  ) {
    const texture = createEdgeTexture();
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;

    this.chunkMaterial = new THREE.MeshLambertMaterial({
      map: texture,
      vertexColors: true,
    });

    const slabTexture = createGridTexture();
    const slabGeometry = new THREE.BoxGeometry(1, 1, 1);
    const slabMaterials = [
      new THREE.MeshLambertMaterial({ color: 0xd8d8d8 }),
      new THREE.MeshLambertMaterial({ color: 0xd8d8d8 }),
      new THREE.MeshLambertMaterial({ map: slabTexture }),
      new THREE.MeshLambertMaterial({ color: 0xd8d8d8 }),
      new THREE.MeshLambertMaterial({ color: 0xd8d8d8 }),
      new THREE.MeshLambertMaterial({ color: 0xd8d8d8 }),
    ];
    this.slabMesh = new THREE.InstancedMesh(
      slabGeometry,
      slabMaterials,
      MAX_VISIBLE_CHUNKS,
    );
    this.slabMesh.count = 0;
    this.slabMesh.receiveShadow = true;
    this.slabMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // It contains only nearby chunks and is one draw call; rebuilding a correct
    // per-instance bound every movement step costs more than drawing it.
    this.slabMesh.frustumCulled = false;
    this.group.add(this.slabMesh);
  }

  /** Switch a chunk between the implicit slab and editable greedy mesh. */
  setChunkEdited(chunkX: number, chunkZ: number, value: boolean): void {
    const key = chunkKey(chunkX, chunkZ);
    if (value) {
      if (this.edited.has(key)) return;
      this.edited.add(key);
    } else {
      if (!this.edited.delete(key)) return;
      this.removeMesh(key);
    }
    this.markChunkAndNeighborsDirty(chunkX, chunkZ);
    this.visibilityDirty = true;
  }

  /** A changed cell can affect its chunk and a neighbor face at chunk edges. */
  invalidate(x: number, z: number): void {
    const chunkX = Math.floor(x / CHUNK_SIZE);
    const chunkZ = Math.floor(z / CHUNK_SIZE);
    this.dirty.add(chunkKey(chunkX, chunkZ));
    if (x % CHUNK_SIZE === 0 && chunkX > 0) {
      this.dirty.add(chunkKey(chunkX - 1, chunkZ));
    }
    if (x % CHUNK_SIZE === CHUNK_SIZE - 1 && chunkX + 1 < CHUNKS_X) {
      this.dirty.add(chunkKey(chunkX + 1, chunkZ));
    }
    if (z % CHUNK_SIZE === 0 && chunkZ > 0) {
      this.dirty.add(chunkKey(chunkX, chunkZ - 1));
    }
    if (z % CHUNK_SIZE === CHUNK_SIZE - 1 && chunkZ + 1 < CHUNKS_Z) {
      this.dirty.add(chunkKey(chunkX, chunkZ + 1));
    }
  }

  invalidateChunk(chunkX: number, chunkZ: number): void {
    this.markChunkAndNeighborsDirty(chunkX, chunkZ);
  }

  /** Horizontal block radius used for chunk culling (runtime-adjustable). */
  setRenderDistanceBlocks(blocks: number): void {
    const chunks = Math.max(1, Math.ceil(blocks / CHUNK_SIZE));
    if (chunks === this.renderDistanceChunks) return;
    this.renderDistanceChunks = chunks;
    this.visibilityDirty = true;
  }

  getRenderDistanceBlocks(): number {
    return this.renderDistanceChunks * CHUNK_SIZE;
  }

  /** Update visible chunks only after crossing a chunk boundary. */
  update(playerX: number, playerZ: number): void {
    const chunkX = Math.floor(playerX / CHUNK_SIZE);
    const chunkZ = Math.floor(playerZ / CHUNK_SIZE);
    if (chunkX !== this.centerChunkX || chunkZ !== this.centerChunkZ) {
      this.centerChunkX = chunkX;
      this.centerChunkZ = chunkZ;
      this.visibilityDirty = true;
    }
    if (this.visibilityDirty) this.syncVisibleChunks();
    this.rebuildDirtyVisibleChunks();
  }

  private syncVisibleChunks(): void {
    this.visibilityDirty = false;
    const visible = new Set<number>();
    let slabCount = 0;
    const radius = this.renderDistanceChunks;

    for (let dz = -radius; dz <= radius; dz++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (dx * dx + dz * dz > radius * radius) continue;
        const chunkX = this.centerChunkX + dx;
        const chunkZ = this.centerChunkZ + dz;
        if (!validChunk(chunkX, chunkZ)) continue;
        const key = chunkKey(chunkX, chunkZ);
        visible.add(key);

        if (this.edited.has(key)) {
          if (!this.meshes.has(key)) this.dirty.add(key);
          continue;
        }

        this.tmpPosition.set(
          chunkX * CHUNK_SIZE + CHUNK_SIZE / 2,
          WORLD_MIN_Y + BASE_DEPTH / 2,
          chunkZ * CHUNK_SIZE + CHUNK_SIZE / 2,
        );
        this.tmpMatrix.compose(
          this.tmpPosition,
          this.tmpQuaternion,
          this.tmpScale,
        );
        this.slabMesh.setMatrixAt(slabCount++, this.tmpMatrix);
      }
    }

    this.slabMesh.count = slabCount;
    this.slabMesh.instanceMatrix.needsUpdate = true;

    for (const key of [...this.meshes.keys()]) {
      if (!visible.has(key) || !this.edited.has(key)) this.removeMesh(key);
    }
  }

  private rebuildDirtyVisibleChunks(): void {
    for (const key of [...this.dirty]) {
      const [chunkX, chunkZ] = unpackChunkKey(key);
      if (!this.isVisible(chunkX, chunkZ)) continue;
      this.dirty.delete(key);
      if (!this.edited.has(key)) continue;
      this.removeMesh(key);
      const geometry = buildGreedyGeometry(chunkX, chunkZ, this.getBlock);
      if (geometry.getAttribute('position').count === 0) {
        geometry.dispose();
        continue;
      }
      const mesh = new THREE.Mesh(geometry, this.chunkMaterial);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = true;
      this.meshes.set(key, mesh);
      this.group.add(mesh);
    }
  }

  private isVisible(chunkX: number, chunkZ: number): boolean {
    const dx = chunkX - this.centerChunkX;
    const dz = chunkZ - this.centerChunkZ;
    return dx * dx + dz * dz <= this.renderDistanceChunks ** 2;
  }

  private markChunkAndNeighborsDirty(chunkX: number, chunkZ: number): void {
    for (const [dx, dz] of [[0, 0], [-1, 0], [1, 0], [0, -1], [0, 1]]) {
      const x = chunkX + dx;
      const z = chunkZ + dz;
      if (validChunk(x, z)) this.dirty.add(chunkKey(x, z));
    }
  }

  private removeMesh(key: number): void {
    const mesh = this.meshes.get(key);
    if (!mesh) return;
    this.group.remove(mesh);
    mesh.geometry.dispose();
    this.meshes.delete(key);
  }
}

function buildGreedyGeometry(
  chunkX: number,
  chunkZ: number,
  getBlock: (x: number, y: number, z: number) => number,
): THREE.BufferGeometry {
  const dims = [CHUNK_SIZE, Y_SIZE, CHUNK_SIZE];
  const origin = [chunkX * CHUNK_SIZE, WORLD_MIN_Y, chunkZ * CHUNK_SIZE];
  const positions: number[] = [];
  const normals: number[] = [];
  const colors: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const mask: Array<MaskCell | null> = [];
  const x = [0, 0, 0];
  const q = [0, 0, 0];

  const sample = (local: number[]): number =>
    getBlock(
      origin[0] + local[0],
      origin[1] + local[1],
      origin[2] + local[2],
    );

  for (let d = 0; d < 3; d++) {
    const u = (d + 1) % 3;
    const v = (d + 2) % 3;
    q[0] = q[1] = q[2] = 0;
    q[d] = 1;
    x[d] = -1;

    while (x[d] < dims[d]) {
      let n = 0;
      for (x[v] = 0; x[v] < dims[v]; x[v]++) {
        for (x[u] = 0; x[u] < dims[u]; x[u]++) {
          const a = sample(x);
          const next = [x[0] + q[0], x[1] + q[1], x[2] + q[2]];
          const b = sample(next);

          if (x[d] === -1) {
            mask[n++] =
              a === BLOCK_AIR && b !== BLOCK_AIR
                ? { type: b, normal: -1 }
                : null;
          } else if (x[d] === dims[d] - 1) {
            mask[n++] =
              a !== BLOCK_AIR && b === BLOCK_AIR
                ? { type: a, normal: 1 }
                : null;
          } else if ((a === BLOCK_AIR) === (b === BLOCK_AIR)) {
            mask[n++] = null;
          } else {
            mask[n++] =
              a !== BLOCK_AIR
                ? { type: a, normal: 1 }
                : { type: b, normal: -1 };
          }
        }
      }

      x[d]++;
      n = 0;
      for (let j = 0; j < dims[v]; j++) {
        for (let i = 0; i < dims[u];) {
          const cell = mask[n];
          if (!cell) {
            i++;
            n++;
            continue;
          }

          let width = 1;
          while (
            i + width < dims[u] &&
            sameMask(mask[n + width], cell)
          ) {
            width++;
          }

          let height = 1;
          outer: while (j + height < dims[v]) {
            for (let k = 0; k < width; k++) {
              if (!sameMask(mask[n + k + height * dims[u]], cell)) break outer;
            }
            height++;
          }

          x[u] = i;
          x[v] = j;
          emitQuad(
            x,
            d,
            u,
            v,
            width,
            height,
            cell,
            origin,
            positions,
            normals,
            colors,
            uvs,
            indices,
          );

          for (let h = 0; h < height; h++) {
            for (let k = 0; k < width; k++) {
              mask[n + k + h * dims[u]] = null;
            }
          }
          i += width;
          n += width;
        }
      }
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();
  return geometry;
}

function emitQuad(
  x: number[],
  d: number,
  u: number,
  v: number,
  width: number,
  height: number,
  cell: MaskCell,
  origin: number[],
  positions: number[],
  normals: number[],
  colors: number[],
  uvs: number[],
  indices: number[],
): void {
  const base = positions.length / 3;
  const p = [x[0] + origin[0], x[1] + origin[1], x[2] + origin[2]];
  const du = [0, 0, 0];
  const dv = [0, 0, 0];
  du[u] = width;
  dv[v] = height;
  const corners =
    cell.normal > 0
      ? [p, add(p, du), add(add(p, du), dv), add(p, dv)]
      : [p, add(p, dv), add(add(p, du), dv), add(p, du)];
  const normal = [0, 0, 0];
  normal[d] = cell.normal;
  const color = new THREE.Color(blockTypeById(cell.type).color);

  for (const corner of corners) {
    positions.push(corner[0], corner[1], corner[2]);
    normals.push(normal[0], normal[1], normal[2]);
    colors.push(color.r, color.g, color.b);
  }
  if (cell.normal > 0) {
    uvs.push(0, 0, width, 0, width, height, 0, height);
  } else {
    // Negative faces reverse the vertex winding (dv is the first edge), so
    // their UVs must follow that same order or merged rectangles stretch and
    // swap their block-grid dimensions.
    uvs.push(0, 0, 0, height, width, height, width, 0);
  }
  indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

function sameMask(a: MaskCell | null | undefined, b: MaskCell): boolean {
  return a?.type === b.type && a.normal === b.normal;
}

function add(a: number[], b: number[]): number[] {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

function chunkKey(chunkX: number, chunkZ: number): number {
  return chunkX + chunkZ * CHUNKS_X;
}

function unpackChunkKey(key: number): [number, number] {
  return [key % CHUNKS_X, Math.floor(key / CHUNKS_X)];
}

function validChunk(chunkX: number, chunkZ: number): boolean {
  return chunkX >= 0 && chunkX < CHUNKS_X && chunkZ >= 0 && chunkZ < CHUNKS_Z;
}

function createEdgeTexture(): THREE.Texture {
  const px = 16;
  const canvas = document.createElement('canvas');
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, px, px);
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, px - 1, px - 1);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function createGridTexture(): THREE.Texture {
  const texture = createEdgeTexture();
  texture.repeat.set(CHUNK_SIZE, CHUNK_SIZE);
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = 8;
  return texture;
}
